import {
  Badge,
  Button,
  ButtonLink,
  CircleQuestionIcon,
  FileUploadField,
  FileUploadFieldController,
  RadioFieldController,
  ScheduledDateTimeFieldController,
  Stack,
  TextAreaFieldController,
  TextInputFieldController,
} from '@fukuri-admins/ui/common';
import { ROUTES } from '~/utils/routes';
import { PUBLISH_SETTING_OPTIONS, PUBLISH_TYPE } from '../constants';
import styles from './styles.module.scss';

type ExistingAttachment = {
  blobSignedId: string;
  name: string;
  size?: number;
  url: string;
};

type PublishType = (typeof PUBLISH_TYPE)[keyof typeof PUBLISH_TYPE];

type Props = {
  existingAttachments: ExistingAttachment[];
  /** 社内報のタイトル */
  title: string;
  /** 公開済み記事かどうか（true の場合は公開設定セクションを非表示） */
  isPublished: boolean;
  publishMode: PublishType;
  isMutating: boolean;
  onSubmit: React.ComponentProps<'form'>['onSubmit'];
  onRemoveExistingAttachment: (blobSignedId: string) => void;
  /** メイン画像が明示的に削除されたときのコールバック */
  onRemoveMainImage: () => void;
  /** 既存メイン画像の URL（初期プレビュー表示用） */
  mainImageUrl: string | undefined;
  /** 新規追加ファイルの現在値 */
  attachedFiles: File[];
  /** ファイルアップロード中フラグ */
  isFileUploading: boolean;
  /** ファイルアップロード非活性フラグ */
  isFileUploadDisabled: boolean;
  /** ファイル変更ハンドラ */
  onFilesChange: (files: File[]) => void;
};

/**
 * 社内報編集フォームの Presentation コンポーネント
 */
export const EditInternalNewsPresentation: React.FC<Props> = ({
  existingAttachments,
  title,
  isPublished,
  publishMode,
  isMutating,
  onSubmit,
  onRemoveExistingAttachment,
  onRemoveMainImage,
  mainImageUrl,
  attachedFiles,
  isFileUploading,
  isFileUploadDisabled,
  onFilesChange,
}) => (
  <form onSubmit={onSubmit} className={styles.form}>
    <Stack direction='column' gap={24}>
      <div className={styles.titleRow}>
        <Badge
          size='lg'
          variant='subtle'
          status={isPublished ? 'success' : 'info'}
          label={isPublished ? '公開済み' : '公開予定'}
        />
        <h2 className={styles.formTitle}>{title}の編集</h2>
        <a
          href={ROUTES.COMPANY_NEWSLETTER_GUIDE}
          target='_blank'
          rel='noopener noreferrer'
          className={styles.guideLink}
          aria-label='ガイドを確認'
        >
          <CircleQuestionIcon size='md' className={styles.guideLinkIcon} aria-hidden />
        </a>
      </div>
      <div className={styles.formContainer}>
        <Stack direction='column' gap={24}>
          <TextInputFieldController
            name='title'
            fieldWrapperProps={{
              labelType: 'required',
              labelText: 'タイトル',
            }}
            textInput={{
              type: 'text',
              placeholder: 'タイトルを入力してください',
            }}
          />

          <TextAreaFieldController
            name='body'
            fieldWrapperProps={{
              labelType: 'required',
              labelText: '本文',
              beforeMessage: 'URLを入力すると、自動的にクリックできるリンクになります。',
            }}
            textArea={{
              placeholder: '社内報の本文を入力',
            }}
          />

          {/* 公開設定: 公開済み記事は非表示 */}
          {!isPublished && (
            <Stack direction='column' gap={8}>
              <RadioFieldController
                name='publishMode'
                fieldWrapperProps={{
                  labelType: 'required',
                  labelText: '公開設定',
                }}
                radioGroup={{
                  options: PUBLISH_SETTING_OPTIONS,
                  direction: 'column',
                  gap: 8,
                }}
              />
              <div className={styles.scheduledDateTimeWrapper}>
                <ScheduledDateTimeFieldController
                  disabled={publishMode === PUBLISH_TYPE.IMMEDIATELY}
                />
              </div>
            </Stack>
          )}

          <FileUploadFieldController
            name='mainImage'
            fileUpload={{
              type: 'image',
              accept: ['jpg', 'jpeg', 'png', 'gif', 'heic'],
              maxSizeMb: 10,
              defaultFile: mainImageUrl != null ? { url: mainImageUrl } : undefined,
            }}
            fieldWrapperProps={{
              labelText: 'メインビジュアル',
              beforeMessage: '記事の上部に表示されるメイン画像',
            }}
            onRemove={onRemoveMainImage}
          />

          <FileUploadField
            fileUpload={{
              type: 'file',
              isMultiple: true,
              accept: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png'],
              maxSizeMb: 10,
              value: attachedFiles,
              onChange: onFilesChange,
              isUploading: isFileUploading,
              disabled: isFileUploadDisabled,
              defaultFiles: existingAttachments.map(a => ({ name: a.name })),
              onDefaultFileRemove: index =>
                onRemoveExistingAttachment(existingAttachments[index].blobSignedId),
            }}
            fieldWrapperProps={{
              labelText: 'ファイル',
              beforeMessage: 'PDFと画像のアップロードが可能。ファイルサイズは10MBまで。',
            }}
          />
        </Stack>
      </div>

      <Stack direction='row' gap={8}>
        <ButtonLink to={ROUTES.INTERNAL_NEWS.INDEX} variant='outline' size='md'>
          キャンセル
        </ButtonLink>
        <Button type='submit' size='md' disabled={isMutating || isFileUploading}>
          保存
        </Button>
      </Stack>
    </Stack>
  </form>
);
